import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { getDatabase, ref, onValue } from "firebase/database";
import { getFortpixelProjectsPath } from "../../utils/fortpixelData";

export function TycoonSignsPage() {
  const { isAdmin } = useOutletContext();
  const [projects, setProjects] = useState({});
  const [selectedProject, setSelectedProject] = useState("");
  const [purchaseData, setPurchaseData] = useState("");
  const [enhancedData, setEnhancedData] = useState("");
  const [enhancedCopySuccess, setEnhancedCopySuccess] = useState(false);
  const [idMap, setIdMap] = useState({});
  const [nameMap, setNameMap] = useState({});
  const [debug, setDebug] = useState(false);

  // New state for purchase pad to sign data conversion
  const [purchasePadData, setPurchasePadData] = useState("");
  const [signData, setSignData] = useState("");
  const [signDataCopySuccess, setSignDataCopySuccess] = useState(false);

  // New state for updating texture indices in map data
  const [mapData, setMapData] = useState("");
  const [updatedMapData, setUpdatedMapData] = useState("");
  const [mapDataCopySuccess, setMapDataCopySuccess] = useState(false);

  // New state for extracting item names from map data
  const [extractMapData, setExtractMapData] = useState("");
  const [extractedNames, setExtractedNames] = useState("");
  const [extractedNamesCopySuccess, setExtractedNamesCopySuccess] =
    useState(false);

  // New state for numbered list
  const [numberedListData, setNumberedListData] = useState("");
  const [numberedList, setNumberedList] = useState("");
  const [numberedListCopySuccess, setNumberedListCopySuccess] = useState(false);

  // New state for device quest array
  const [deviceQuestInput, setDeviceQuestInput] = useState("");
  const [deviceQuestArray, setDeviceQuestArray] = useState("");
  const [deviceQuestArrayCopySuccess, setDeviceQuestArrayCopySuccess] =
    useState(false);

  // Toggle states for sections
  const [showTextureIndicesSection, setShowTextureIndicesSection] =
    useState(false);
  const [showSignDataSection, setShowSignDataSection] = useState(false);
  const [showNumberedListSection, setShowNumberedListSection] = useState(false);
  const [showDeviceQuestSection, setShowDeviceQuestSection] = useState(false);

  // Hardcoded prefix styles
  const PREFIX_STYLES = {
    Money: { index: 0, color: 1, hex: "00DC00FF" },
    Billicoin: { index: 2, color: 0, hex: "D87200FF" },
    Battlepass: { index: 3, color: 0, hex: "0161FFFF" },
    Rebirth: { index: 1, color: 0, hex: "9300FFFF" },
  };

  // Fetch tycoon requires data
  useEffect(() => {
    const db = getDatabase();
    let unsubscribe = () => {};

    const initialize = async () => {
      const dataRef = ref(db, getFortpixelProjectsPath());

      unsubscribe = onValue(
        dataRef,
        (snapshot) => {
          const data = snapshot.val();
          if (data) {
            setProjects(data);
            // Set the first project as default selected
            if (Object.keys(data).length > 0 && !selectedProject) {
              setSelectedProject(Object.keys(data)[0]);
            }

            // Generate ID map and name map for all projects
            const allIdMaps = {};
            const allNameMaps = {};

            Object.keys(data).forEach((project) => {
              const projectData = data[project];
              const projectIdMap = {};
              const projectNameMap = {};

              Object.entries(projectData)
                .filter(([id]) => id !== "__PLACEHOLDER__")
                .forEach(([id, name]) => {
                  projectIdMap[id] = {
                    name,
                    id, // Store the actual ID
                  };

                  // Store name in lowercase for case-insensitive matching
                  projectNameMap[name.toLowerCase()] = {
                    id, // Store the actual ID
                  };
                });

              allIdMaps[project] = projectIdMap;
              allNameMaps[project] = projectNameMap;
            });

            setIdMap(allIdMaps);
            setNameMap(allNameMaps);
          }
        },
        (error) => {
          console.error("Failed to load data:", error.message);
        },
      );
    };

    initialize().catch((error) => {
      console.error("Failed to initialize projects migration:", error);
    });

    return () => unsubscribe();
  }, [selectedProject]);

  // Handle project selection change
  const handleProjectChange = (e) => {
    setSelectedProject(e.target.value);
    setEnhancedData("");
    setEnhancedCopySuccess(false);
    setUpdatedMapData("");
    setMapDataCopySuccess(false);
    setExtractedNames("");
    setExtractedNamesCopySuccess(false);
  };

  // Handle purchase data input
  const handlePurchaseDataChange = (e) => {
    setPurchaseData(e.target.value);
    setEnhancedData("");
    setEnhancedCopySuccess(false);
  };

  // Handle map data input
  const handleMapDataChange = (e) => {
    setMapData(e.target.value);
    setUpdatedMapData("");
    setMapDataCopySuccess(false);
  };

  // Handle extract map data input
  const handleExtractMapDataChange = (e) => {
    setExtractMapData(e.target.value);
    setExtractedNames("");
    setExtractedNamesCopySuccess(false);
  };

  // Handle numbered list data input
  const handleNumberedListDataChange = (e) => {
    setNumberedListData(e.target.value);
    setNumberedList("");
    setNumberedListCopySuccess(false);
  };

  // Handle device quest input
  const handleDeviceQuestInputChange = (e) => {
    setDeviceQuestInput(e.target.value);
    setDeviceQuestArray("");
    setDeviceQuestArrayCopySuccess(false);
  };

  // Generate device quest array format
  const handleGenerateDeviceQuestArray = () => {
    if (!deviceQuestInput) return;

    try {
      // Process the input - split by newlines, remove empty lines, and trim whitespace
      const numbers = deviceQuestInput
        .split(/\n/)
        .map((line) => line.trim())
        .filter((line) => line !== "" && !isNaN(line));

      // Format as DeviceQuestArray
      const formattedArray = `DeviceQuestArray : []int = array{${numbers.join(
        ", "
      )}}`;
      setDeviceQuestArray(formattedArray);
    } catch (error) {
      console.error("Error generating device quest array:", error);
      setDeviceQuestArray(`Error: ${error.message}`);
    }
  };

  // Generate numbered list from map data
  const handleGenerateNumberedList = () => {
    if (!numberedListData) return;

    try {
      // This assumes the input data is in the map format shown in the example
      const regex =
        /(\d+) => purchase_data \{[\s\S]*?ID := (\d+)[\s\S]*?Name := "([^"]*)"/g;
      const items = [];

      let match;
      while ((match = regex.exec(numberedListData)) !== null) {
        // Use the ID from the data, not the map key
        const id = match[2];
        const name = match[3].trim() === "" ? "placeholder" : match[3];
        items.push({ id, name });
      }

      // Sort by ID if needed
      items.sort((a, b) => parseInt(a.id) - parseInt(b.id));

      // Format as numbered list
      const formattedList = items
        .map((item) => `${item.id}\t${item.name}`)
        .join("\n");
      setNumberedList(formattedList);
    } catch (error) {
      console.error("Error generating numbered list:", error);
      setNumberedList(`Error: ${error.message}`);
    }
  };

  // Extract item names from map data
  const handleExtractNames = () => {
    if (!extractMapData) return;

    try {
      // This assumes the input data is in the map format shown in the example
      const nameRegex = /Name := "([^"]+)"/g;
      const names = new Set();

      let match;
      while ((match = nameRegex.exec(extractMapData)) !== null) {
        names.add(match[1]);
      }

      // Convert to sorted array and join with newlines
      const sortedNames = Array.from(names).sort();
      setExtractedNames(sortedNames.join("\n"));
    } catch (error) {
      console.error("Error extracting names:", error);
      setExtractedNames(`Error: ${error.message}`);
    }
  };

  // Process purchase data to add texture indices
  const handleEnhancePurchaseData = () => {
    if (!purchaseData || !selectedProject || !nameMap[selectedProject]) return;

    try {
      let debugInfo = {};

      // This assumes the input data is in the specific format shown in the example
      let processedData = purchaseData;

      // Find all instances of Name := "Something" and add TextureIndex lines
      const regex = /ID := (\d+)[\s\S]*?Name := "([^"]+)"/g;

      processedData = processedData.replace(regex, (match, id, name) => {
        let textureIdx1 = 0; // Default to 0 for TextureIndex1
        let textureIdx2 = 0; // Default value

        // Check if the name matches something in the tycoon requires data (case-insensitive)
        const nameLower = name.toLowerCase();

        if (nameMap[selectedProject][nameLower]) {
          // Use the actual ID from tycoon requires for TextureIndex2
          textureIdx2 = parseInt(nameMap[selectedProject][nameLower].id);

          if (debug) {
            debugInfo[name] = {
              match: true,
              id: textureIdx2,
            };
          }
        } else if (debug) {
          debugInfo[name] = {
            match: false,
          };
        }

        // Replace the match to include TextureIndex lines
        return `ID := ${id}\n            Name := "${name}"\n            TextureIndex1 := ${textureIdx1}.0\n            TextureIndex2 := ${textureIdx2}.0`;
      });

      if (debug) {
        console.log("Name matching debug:", debugInfo);
      }

      setEnhancedData(processedData);
    } catch (error) {
      console.error("Error processing purchase data:", error);
      setEnhancedData(`Error processing data: ${error.message}`);
    }
  };

  // Process map data to update texture indices
  const handleUpdateMapTextureIndices = () => {
    if (!mapData || !selectedProject || !nameMap[selectedProject]) return;

    try {
      let debugInfo = {};

      // This assumes the input data is in the map format shown in the example
      let processedData = mapData;

      // Match each entry in the purchase_data map
      const entryRegex = /(\d+) => purchase_data \{([\s\S]*?)\}/g;

      processedData = processedData.replace(
        entryRegex,
        (match, id, content) => {
          // Extract the Name from the content
          const nameMatch = content.match(/Name := "([^"]+)"/);
          if (!nameMatch) return match; // If no name found, return unchanged

          const name = nameMatch[1];
          const nameLower = name.toLowerCase();

          let textureIdx1 = 0; // Default to 0 for TextureIndex1
          let textureIdx2 = 0; // Default value

          // Check if the name matches something in the tycoon requires data
          if (nameMap[selectedProject][nameLower]) {
            // Use the actual ID from tycoon requires for TextureIndex2
            textureIdx2 = parseInt(nameMap[selectedProject][nameLower].id);

            if (debug) {
              debugInfo[name] = {
                match: true,
                id: textureIdx2,
              };
            }
          } else if (debug) {
            debugInfo[name] = {
              match: false,
            };
          }

          // Update the TextureIndex lines in the content
          let updatedContent = content;

          // Check if TextureIndex lines already exist
          const hasTextureIndices =
            content.includes("TextureIndex1") &&
            content.includes("TextureIndex2");

          if (hasTextureIndices) {
            // Update existing TextureIndex lines
            updatedContent = updatedContent.replace(
              /TextureIndex1 := [^\n]+\n\s+TextureIndex2 := [^\n]+/g,
              `TextureIndex1 := ${textureIdx1}.0\n            TextureIndex2 := ${textureIdx2}.0`
            );
          } else {
            // Add TextureIndex lines after Name
            updatedContent = updatedContent.replace(
              /(Name := "[^"]+")/,
              `$1\n            TextureIndex1 := ${textureIdx1}.0\n            TextureIndex2 := ${textureIdx2}.0`
            );
          }

          return `${id} => purchase_data {${updatedContent}}`;
        }
      );

      if (debug) {
        console.log("Map texture update debug:", debugInfo);
      }

      setUpdatedMapData(processedData);
    } catch (error) {
      console.error("Error updating map texture indices:", error);
      setUpdatedMapData(`Error: ${error.message}`);
    }
  };

  // Handle purchase pad data input
  const handlePurchasePadDataChange = (e) => {
    setPurchasePadData(e.target.value);
    setSignData("");
    setSignDataCopySuccess(false);
  };

  // Convert purchase pad data to sign data format
  const handleConvertToSignData = () => {
    if (!purchasePadData) return;

    try {
      // Parse the JSON input
      const purchasePadObj = JSON.parse(purchasePadData);
      let result = "var TycoonSignData : [int]tycoon_sign_data = map{\n";

      // Helper function to ensure float format
      const formatAsFloat = (value) => {
        // Check if the value already has a decimal point
        if (String(value).includes(".")) {
          return value;
        }
        // Otherwise add .0
        return `${value}.0`;
      };

      Object.entries(purchasePadObj).forEach(([key, data], index) => {
        // Extract numeric ID from key (e.g., "PurchasePad1" -> 1)
        const idMatch = key.match(/\d+/);
        const id = idMatch ? idMatch[0] : index + 1;

        // Get prefix style data using prefixStyle property
        const prefixStyle = data.prefixStyle || "Money"; // Default to Money if not specified
        const prefixData = PREFIX_STYLES[prefixStyle] || PREFIX_STYLES["Money"];

        result += `        ${id} => tycoon_sign_data{\n`;
        result += `            Price := ${formatAsFloat(
          data.inputNumber || data.price || 0
        )},\n`;
        result += `            PrefixIndex := ${formatAsFloat(
          prefixData.index
        )},\n`;
        result += `            PrefixColor := ${formatAsFloat(
          prefixData.color
        )},\n`;
        result += `            HasDecimal := ${formatAsFloat(
          data.hasDecimal || 0
        )},\n`;
        result += `            DecimalPlacement := ${formatAsFloat(
          data.decimalPlacement || 0
        )},\n`;
        result += `            HasSuffix := ${formatAsFloat(
          data.hasSuffix || 0
        )},\n`;
        result += `            SuffixIndex := ${formatAsFloat(
          data.suffixIndex || 0
        )},\n`;
        result += `            PriceColor := "${prefixData.hex}"\n`;
        result += `        }${
          index < Object.keys(purchasePadObj).length - 1 ? "," : ""
        }\n`;
      });

      result += "    }";
      setSignData(result);
    } catch (error) {
      console.error("Error converting purchase pad data:", error);
      setSignData(`Error: ${error.message}`);
    }
  };

  // Copy functions
  const handleCopyEnhanced = () => {
    if (!enhancedData) return;

    navigator.clipboard
      .writeText(enhancedData)
      .then(() => {
        setEnhancedCopySuccess(true);
        setTimeout(() => setEnhancedCopySuccess(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy enhanced data:", err);
      });
  };

  const handleCopySignData = () => {
    if (!signData) return;

    navigator.clipboard
      .writeText(signData)
      .then(() => {
        setSignDataCopySuccess(true);
        setTimeout(() => setSignDataCopySuccess(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy sign data:", err);
      });
  };

  const handleCopyMapData = () => {
    if (!updatedMapData) return;

    navigator.clipboard
      .writeText(updatedMapData)
      .then(() => {
        setMapDataCopySuccess(true);
        setTimeout(() => setMapDataCopySuccess(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy map data:", err);
      });
  };

  const handleCopyExtractedNames = () => {
    if (!extractedNames) return;

    navigator.clipboard
      .writeText(extractedNames)
      .then(() => {
        setExtractedNamesCopySuccess(true);
        setTimeout(() => setExtractedNamesCopySuccess(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy extracted names:", err);
      });
  };

  const handleCopyNumberedList = () => {
    if (!numberedList) return;

    navigator.clipboard
      .writeText(numberedList)
      .then(() => {
        setNumberedListCopySuccess(true);
        setTimeout(() => setNumberedListCopySuccess(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy numbered list:", err);
      });
  };

  const handleCopyDeviceQuestArray = () => {
    if (!deviceQuestArray) return;

    navigator.clipboard
      .writeText(deviceQuestArray)
      .then(() => {
        setDeviceQuestArrayCopySuccess(true);
        setTimeout(() => setDeviceQuestArrayCopySuccess(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy device quest array:", err);
      });
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-200">Tycoon Signs</h2>
        <p className="mt-2 text-sm text-gray-400">
          Add texture indices to purchase data
        </p>
      </div>

      {/* Project selection for all features */}
      <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-6 mb-8">
        <h3 className="text-xl font-semibold text-gray-200 mb-4">
          Select Tycoon Requires Data Source
        </h3>
        <div className="space-y-6">
          <div className="space-y-2">
            <label
              htmlFor="project-select"
              className="block text-sm font-medium text-gray-300"
            >
              Choose the tycoon requires data to use for all conversions
            </label>
            <select
              id="project-select"
              value={selectedProject}
              onChange={handleProjectChange}
              className="w-full bg-[#27272A] text-gray-200 border border-gray-700 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-fortpixel-gold"
            >
              <option value="">Select a project</option>
              {Object.keys(projects).map((project) => (
                <option key={project} value={project}>
                  {project}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              This data source will be used for all texture index lookups below.
            </p>
          </div>
        </div>
      </div>

      {/* Extract Names from Map Data Section */}
      <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-6 mb-8">
        <h3 className="text-xl font-semibold text-gray-200 mb-4">
          Extract Item Names
        </h3>
        <div className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Paste Map Data
            </label>
            <textarea
              value={extractMapData}
              onChange={handleExtractMapDataChange}
              className="w-full h-60 bg-[#27272A] text-gray-200 border border-gray-700 rounded-md py-2 px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-fortpixel-gold"
              placeholder="Paste your map data here... (Data: [int]purchase_data = map {...)"
            />
          </div>

          <div>
            <button
              onClick={handleExtractNames}
              disabled={!extractMapData}
              className="px-4 py-2 bg-fortpixel-gold hover:bg-yellow-600 text-black font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Extract Names
            </button>
          </div>

          {/* Extracted names output */}
          {extractedNames && (
            <div className="space-y-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <label className="block text-sm font-medium text-gray-300">
                  Extracted Item Names
                </label>
                <button
                  onClick={handleCopyExtractedNames}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md"
                >
                  {extractedNamesCopySuccess ? "Copied!" : "Copy"}
                </button>
              </div>
              <div className="bg-[#27272A] p-4 rounded-md max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-200 whitespace-pre-wrap font-mono">
                  {extractedNames}
                </pre>
              </div>
            </div>
          )}

          <div className="bg-gray-800 p-4 rounded-md border border-gray-700">
            <h4 className="text-sm font-medium text-gray-300 mb-2">
              How it works
            </h4>
            <p className="text-xs text-gray-400">
              This tool extracts all unique item names from the map data and
              returns them as a sorted list. It's useful for getting a quick
              overview of all items in your tycoon. The names are alphabetically
              sorted and each name appears only once, even if it occurs multiple
              times in the original data.
            </p>
          </div>
        </div>
      </div>

      {/* Generate Numbered List Section */}
      <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-6 mb-8">
        <h3 className="text-xl font-semibold text-gray-200 mb-4">
          Generate Numbered List
        </h3>
        <div className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Paste Map Data
            </label>
            <textarea
              value={numberedListData}
              onChange={handleNumberedListDataChange}
              className="w-full h-60 bg-[#27272A] text-gray-200 border border-gray-700 rounded-md py-2 px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-fortpixel-gold"
              placeholder="Paste your map data here... (Data: [int]purchase_data = map {...)"
            />
          </div>

          <div>
            <button
              onClick={handleGenerateNumberedList}
              disabled={!numberedListData}
              className="px-4 py-2 bg-fortpixel-gold hover:bg-yellow-600 text-black font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Generate Numbered List
            </button>
          </div>

          {/* Numbered list output */}
          {numberedList && (
            <div className="space-y-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <label className="block text-sm font-medium text-gray-300">
                  Numbered List
                </label>
                <button
                  onClick={handleCopyNumberedList}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md"
                >
                  {numberedListCopySuccess ? "Copied!" : "Copy"}
                </button>
              </div>
              <div className="bg-[#27272A] p-4 rounded-md max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-200 whitespace-pre-wrap font-mono">
                  {numberedList}
                </pre>
              </div>
            </div>
          )}

          <div className="bg-gray-800 p-4 rounded-md border border-gray-700">
            <h4 className="text-sm font-medium text-gray-300 mb-2">
              How it works
            </h4>
            <p className="text-xs text-gray-400">
              This tool generates a numbered list from the map data in the
              format "ID [tab] Name". It extracts the ID and name from each
              entry and formats them as a tab-separated list, sorted by ID.
            </p>
          </div>
        </div>
      </div>

      {/* Device Quest Array Section */}
      <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-6 mb-8">
        <h3 className="text-xl font-semibold text-gray-200 mb-4">
          Generate Device Quest Array
        </h3>
        <div className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Paste Device Quest Input
            </label>
            <textarea
              value={deviceQuestInput}
              onChange={handleDeviceQuestInputChange}
              className="w-full h-60 bg-[#27272A] text-gray-200 border border-gray-700 rounded-md py-2 px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-fortpixel-gold"
              placeholder="Enter each ID on a new line, for example:
1
198
3
196
215"
            />
          </div>

          <div>
            <button
              onClick={handleGenerateDeviceQuestArray}
              disabled={!deviceQuestInput}
              className="px-4 py-2 bg-fortpixel-gold hover:bg-yellow-600 text-black font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Generate Device Quest Array
            </button>
          </div>

          {/* Device quest array output */}
          {deviceQuestArray && (
            <div className="space-y-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <label className="block text-sm font-medium text-gray-300">
                  Device Quest Array
                </label>
                <button
                  onClick={handleCopyDeviceQuestArray}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md"
                >
                  {deviceQuestArrayCopySuccess ? "Copied!" : "Copy"}
                </button>
              </div>
              <div className="bg-[#27272A] p-4 rounded-md max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-200 whitespace-pre-wrap font-mono">
                  {deviceQuestArray}
                </pre>
              </div>
            </div>
          )}

          <div className="bg-gray-800 p-4 rounded-md border border-gray-700">
            <h4 className="text-sm font-medium text-gray-300 mb-2">
              How it works
            </h4>
            <p className="text-xs text-gray-400">
              This tool takes a list of numbers (one per line) and converts them
              to a DeviceQuestArray format. It filters out any empty lines and
              non-numeric values and formats the result as "DeviceQuestArray :
              []int = array{(1, 2, 3)}".
            </p>
          </div>
        </div>
      </div>

      {/* Update Map Texture Indices Section */}
      <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-6 mb-8">
        <h3 className="text-xl font-semibold text-gray-200 mb-4">
          Update Texture Indices in Map
        </h3>
        <div className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Paste Map Data
            </label>
            <textarea
              value={mapData}
              onChange={handleMapDataChange}
              className="w-full h-60 bg-[#27272A] text-gray-200 border border-gray-700 rounded-md py-2 px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-fortpixel-gold"
              placeholder="Paste your map data here... (Data: [int]purchase_data = map {...)"
            />
          </div>

          <div>
            <button
              onClick={handleUpdateMapTextureIndices}
              disabled={!mapData || !selectedProject}
              className="px-4 py-2 bg-fortpixel-gold hover:bg-yellow-600 text-black font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Update Texture Indices
            </button>
          </div>

          {/* Updated map data output */}
          {updatedMapData && (
            <div className="space-y-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <label className="block text-sm font-medium text-gray-300">
                  Updated Map Data
                </label>
                <button
                  onClick={handleCopyMapData}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md"
                >
                  {mapDataCopySuccess ? "Copied!" : "Copy"}
                </button>
              </div>
              <div className="bg-[#27272A] p-4 rounded-md max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-200 whitespace-pre-wrap font-mono">
                  {updatedMapData}
                </pre>
              </div>
            </div>
          )}

          <div className="bg-gray-800 p-4 rounded-md border border-gray-700">
            <h4 className="text-sm font-medium text-gray-300 mb-2">
              How it works
            </h4>
            <p className="text-xs text-gray-400">
              This tool updates TextureIndex1 and TextureIndex2 in map data
              based on name matching. TextureIndex1 is set to 0 by default. For
              TextureIndex2, the tool matches the item's name with names in the
              tycoon requires data and uses the actual ID of the matching item.
              It works with existing map formatted purchase data and preserves
              all other fields.
            </p>
          </div>
        </div>
      </div>

      {/* Toggle for Add Texture Indices Section */}
      <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-6 mb-8">
        <button
          onClick={() =>
            setShowTextureIndicesSection(!showTextureIndicesSection)
          }
          className="w-full flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-left"
        >
          <h3 className="text-xl font-semibold text-gray-200">
            Add Texture Indices
          </h3>
          <span className="text-gray-400 text-xl">
            {showTextureIndicesSection ? "▼" : "►"}
          </span>
        </button>

        {showTextureIndicesSection && (
          <div className="space-y-6 mt-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                Paste Purchase Data
              </label>
              <textarea
                value={purchaseData}
                onChange={handlePurchaseDataChange}
                className="w-full h-60 bg-[#27272A] text-gray-200 border border-gray-700 rounded-md py-2 px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-fortpixel-gold"
                placeholder="Paste your purchase data here..."
              />
            </div>

            <div>
              <button
                onClick={handleEnhancePurchaseData}
                disabled={!purchaseData || !selectedProject}
                className="px-4 py-2 bg-fortpixel-gold hover:bg-yellow-600 text-black font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Texture Indices
              </button>
            </div>

            {/* Enhanced data output */}
            {enhancedData && (
              <div className="space-y-2">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <label className="block text-sm font-medium text-gray-300">
                    Enhanced Purchase Data
                  </label>
                  <button
                    onClick={handleCopyEnhanced}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md"
                  >
                    {enhancedCopySuccess ? "Copied!" : "Copy"}
                  </button>
                </div>
                <div className="bg-[#27272A] p-4 rounded-md max-h-96 overflow-y-auto">
                  <pre className="text-sm text-gray-200 whitespace-pre-wrap font-mono">
                    {enhancedData}
                  </pre>
                </div>
              </div>
            )}

            <div className="bg-gray-800 p-4 rounded-md border border-gray-700">
              <h4 className="text-sm font-medium text-gray-300 mb-2">
                How it works
              </h4>
              <p className="text-xs text-gray-400">
                This tool adds TextureIndex1 and TextureIndex2 based on name
                matching. TextureIndex1 is set to 0 by default. For
                TextureIndex2, the tool matches the item's name with names in
                the tycoon requires data and uses the actual ID of the matching
                item in the tycoon requires data. For example, if "Dropper" has
                ID 9 in your tycoon requires list, TextureIndex2 will be set to
                9.0.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Toggle for Convert to Tycoon Sign Data Section */}
      <div className="bg-[#18181B] rounded-xl shadow-xl shadow-black/20 p-6">
        <button
          onClick={() => setShowSignDataSection(!showSignDataSection)}
          className="w-full flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-left"
        >
          <h3 className="text-xl font-semibold text-gray-200">
            Convert to Tycoon Sign Data
          </h3>
          <span className="text-gray-400 text-xl">
            {showSignDataSection ? "▼" : "►"}
          </span>
        </button>

        {showSignDataSection && (
          <div className="space-y-6 mt-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                Paste Purchase Pad JSON Data
              </label>
              <textarea
                value={purchasePadData}
                onChange={handlePurchasePadDataChange}
                className="w-full h-60 bg-[#27272A] text-gray-200 border border-gray-700 rounded-md py-2 px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-fortpixel-gold"
                placeholder='Paste your purchase pad JSON data here... (e.g., {"PurchasePad1": {...}, ...})'
              />
            </div>

            <div>
              <button
                onClick={handleConvertToSignData}
                disabled={!purchasePadData}
                className="px-4 py-2 bg-fortpixel-gold hover:bg-yellow-600 text-black font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Convert to Sign Data
              </button>
            </div>

            {/* Sign data output */}
            {signData && (
              <div className="space-y-2">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <label className="block text-sm font-medium text-gray-300">
                    Tycoon Sign Data
                  </label>
                  <button
                    onClick={handleCopySignData}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md"
                  >
                    {signDataCopySuccess ? "Copied!" : "Copy"}
                  </button>
                </div>
                <div className="bg-[#27272A] p-4 rounded-md max-h-96 overflow-y-auto">
                  <pre className="text-sm text-gray-200 whitespace-pre-wrap font-mono">
                    {signData}
                  </pre>
                </div>
              </div>
            )}

            <div className="bg-gray-800 p-4 rounded-md border border-gray-700">
              <h4 className="text-sm font-medium text-gray-300 mb-2">
                Prefix Style Colors
              </h4>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(PREFIX_STYLES).map(([name, data]) => (
                  <div key={name} className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: `#${data.hex.slice(0, 6)}` }}
                    ></div>
                    <span className="text-xs text-gray-300">
                      {name}: #{data.hex}
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-400">
                This tool converts purchase pad data to tycoon sign data format,
                automatically mapping prefixStyle to the appropriate color hex
                values.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

